import { AmountDisplay } from '../../../../../design-system/amount-display/amount-display';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { Tooltip } from '../../../../../design-system/tooltip/tooltip';
import { TransactionView } from '../../../../../types/entity-types';
import { cn } from '../../../../../utils/cn';
import { getNormalizedAmount } from '../../../../../utils/get-normalized-amount';
import {
  formatTransactionDate,
  getTransactionDisplayInfo,
  truncateAfterSecondWord,
} from '../../../utils/card-transactions-utils';
import { TransactionAvatar } from './transaction-avatar';

type CardTransactionRowProps = {
  transaction: TransactionView;
  onClick?: () => void;
  showBorder?: boolean;
};

export const CardTransactionRow = ({ transaction, onClick, showBorder = false }: CardTransactionRowProps) => {
  const { name, avatarUrl, initials } = getTransactionDisplayInfo(transaction);
  const formattedDate = formatTransactionDate(transaction.fspDate);
  const normalizedAmount = getNormalizedAmount({ amount: transaction.amount, isCredit: transaction.isCredit });
  const truncatedName = truncateAfterSecondWord(name);
  const needsTooltip = truncatedName !== name;

  return (
    <Inline
      align="center"
      justify="between"
      wrap="nowrap"
      className={cn('w-full cursor-pointer px-4 py-3 hover:bg-muted/50', showBorder && 'border-b border-border')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <Inline space={3} align="center" wrap="nowrap" className="min-w-0 flex-1 overflow-hidden">
        <TransactionAvatar avatarUrl={avatarUrl} initials={initials} />

        <Stack space={0} className="min-w-0 flex-1 overflow-hidden">
          {needsTooltip ? (
            <Tooltip
              trigger={<p className="w-full truncate text-sm font-normal text-foreground">{truncatedName}</p>}
              content={name}
              delayDuration={300}
            />
          ) : (
            <p className="w-full truncate text-sm font-normal text-foreground">{name}</p>
          )}

          <p className="text-xs text-muted-foreground">{formattedDate}</p>
        </Stack>
      </Inline>

      <span className="shrink-0">
        <AmountDisplay normalizedAmount={normalizedAmount} currency={transaction.currency} />
      </span>
    </Inline>
  );
};
